import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { UserProfile } from '../../features/user/UserProfile'
import { Button } from '../../features/section-components/Button'
import styles from './MyAccount.module.scss'
import { LoadingIcon } from '../../features/section-components/LoadingIcon'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import JNJFooter from '../../features/jnj-footer/JNJFooter'
import '../../util/i18n'
import Alert from '../../features/error/Alert'
import {
  selectUserCallError,
} from '../../features/user/userSlice'
import { PictureUpload } from '../../features/PictureUpload'
import AccountItemContainer from './components/AccountItemContainer'
import FlagAccountItemContainer from './components/FlagAccountItemContainer'
import LinkAccountItemContainer from './components/LinkAccountItemContainer'

function MyAccountMobile({
  t,
  userObj,
  userProfilePictureData,
  fullName,
  isPageLoading,
  renderData,
  formatPhoneToNational,
  preferredLanguage,
  languageOptions,
  setAlertVisible,
  editAccountComponent,
  toggleEditAccount,
  showEditAccount,
  showProfileInfo,
  handlePhotoDelete,
  phoneNumber,
  editNameStatus,
  editLanguageStatus,
  editSecondaryEmailStatus,
}) {
  const userError = useSelector(selectUserCallError)
  const initials = `${userObj && userObj.firstName ? userObj.firstName[0] : ''
  }${userObj && userObj.lastName ? userObj.lastName[0] : ''}`

  const scrollToTop = () => {
    window.scrollTo(0, 0)
  }

  const location = useLocation()
  useEffect(() => {
    if (location?.state?.editNameStatus || location?.state?.editLanguageStatus
      || location?.state?.editSecondaryEmailStatus) {
      window.history.replaceState({}, document.title, location.pathname)
    }
  }, [])

  return (
    <MarginWrapper>
      <LoadingIcon isLoading={isPageLoading}>
        <div className={styles['mobile-my-account-container']}>
          <div className={styles['mobile-back-home-section']}>
            <Link
              to="/home"
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
              color="black"
            >
              <ArrowBackIosIcon
                className={styles['home-icon']}
                fontSize="inherit"
              />
              <div className={styles['home-text']}>{t('home-tab')}</div>
            </Link>
          </div>
          <div
            className={styles['mobile-section-header-left-align']}
            style={{ marginTop: '25px' }}
          >
            {t('AccountInfoHeader')}
          </div>
          {userError && (
            <Alert
              message={t('error-saveFailed')}
              onClose={() => setAlertVisible(false)}
              alertStyle="error-alert"
              headerText={t('error')}
              blurb
            />
          )}
          {editNameStatus && (
            <Alert
              message={t('success-edit-name')}
              onClose={() => setAlertVisible(false)}
              alertStyle="success-alert"
              headerText={t('success-edit-name-header')}
            />
          )}
          {editLanguageStatus && (
            <Alert
              message={t('success-edit-language')}
              onClose={() => setAlertVisible(false)}
              alertStyle="success-alert"
              headerText={t('success-edit-language-header')}
            />
          )}
          {editSecondaryEmailStatus && (
            <div className={styles['alert-container']}>
              <Alert
                message={t('success-edit-secondary-email')}
                onClose={() => setAlertVisible(false)}
                alertStyle="success-alert"
                headerText={t('success-edit-secondary-header')}
              />
            </div>
          )}
          <div className={styles['mobile-profile-info-container']}>
            <div className={styles['mobile-profile-header-container']}>
              <div className={styles['mobile-profile-picture-container']}>
                <div className={styles['mobile-profile-picture-wrapper']}>
                  <UserProfile
                    isInteractive={false}
                    profilePictureData={userProfilePictureData}
                    initials={initials}
                  />
                </div>
                <div className={styles['mobile-profile-picture-support-text']}>
                  <div className={styles['mobile-upload-button']}>
                    <PictureUpload />
                  </div>
                  <div className={styles['mobile-delete-button']}>
                    {t('support-text-profile-pic')}
                    {userProfilePictureData ? (
                      <Button
                        text={t('button-delete-photo')}
                        buttonStyle="cta-yellow-oval-mobile"
                        disabled={false}
                        icon={<DeleteOutlineIcon />}
                        iconSize="small"
                        onClick={handlePhotoDelete}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
              <div className={styles['account-header-support-text']}>
                <span>{t('support-text-account')}</span>
              </div>
            </div>
            <div className={styles['mobile-user-profile-info-container']}>
              {showProfileInfo && (
                <>
                  <AccountItemContainer
                    label="label-name"
                    link="/edit-name"
                    onClick={scrollToTop}
                    renderData={renderData}
                    t={t}
                    value={fullName}
                    mobile
                    button
                  />

                  <AccountItemContainer
                    label="label-email"
                    onClick={toggleEditAccount}
                    renderData={renderData}
                    t={t}
                    value={userObj.email}
                    mobile
                  />

                  {(userObj.secondEmail || !userObj.secondEmail) && (
                    <AccountItemContainer
                      label="label-second-email"
                      link="/edit-secondary-email"
                      onClick={scrollToTop}
                      renderData={renderData}
                      t={t}
                      value={userObj.secondEmail}
                      mobile
                      button
                    />
                  )}

                  {(phoneNumber || !phoneNumber) && (
                    <LinkAccountItemContainer
                      onClick={scrollToTop}
                      renderData={renderData}
                      t={t}
                      link="/edit-phone"
                      formatPhoneToNational={formatPhoneToNational}
                      phoneNumber={phoneNumber}
                      mobile
                    />
                  )}

                  <FlagAccountItemContainer
                    onClick={scrollToTop}
                    preferredLanguage={preferredLanguage}
                    renderData={renderData}
                    t={t}
                    link="/edit-language"
                    languageOptions={languageOptions}
                    mobile
                  />
                </>
              )}
              {showEditAccount && editAccountComponent}
            </div>
          </div>
          {/* <div
            className={styles['mobile-password-container']}
            style={{ lineHeight: '1.2', marginTop: '40px' }}
          >
            <div className={styles['mobile-section-subheader-left-align']}>
              {t('label-header-password')}
            </div>
            {t('support-text-pass', { email: userObj.email })}
            <div className={styles.buttons}>
              <Grid className={styles['mobile-manage-button']}>
                <Link to="/reset-password" style={{ textDecoration: 'none' }}>
                  <Button
                    text={t('button-reset-password')}
                    buttonStyle="cta-grey-oval"
                  />
                </Link>
              </Grid>
            </div>
          </div> */}
          <JNJFooter> </JNJFooter>
        </div>
      </LoadingIcon>
    </MarginWrapper>
  )
}

MyAccountMobile.propTypes = {
  t: PropTypes.func.isRequired,
  userObj: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    secondEmail: PropTypes.string,
    mobilePhone: PropTypes.string,
    created: PropTypes.string,
    preferredLanguage: PropTypes.string,
  }).isRequired,
  userProfilePictureData: PropTypes.string,
  fullName: PropTypes.string.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
  renderData: PropTypes.func.isRequired,
  formatPhoneToNational: PropTypes.func.isRequired,
  preferredLanguage: PropTypes.string.isRequired,
  languageOptions: PropTypes.objectOf(PropTypes.string).isRequired,
  setAlertVisible: PropTypes.bool.isRequired,
  editAccountComponent: PropTypes.element,
  toggleEditAccount: PropTypes.func.isRequired,
  showEditAccount: PropTypes.bool.isRequired,
  showProfileInfo: PropTypes.bool.isRequired,
  handlePhotoDelete: PropTypes.func.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  editNameStatus: PropTypes.bool.isRequired,
  editLanguageStatus: PropTypes.bool.isRequired,
  editSecondaryEmailStatus: PropTypes.bool.isRequired
}

MyAccountMobile.defaultProps = {
  userProfilePictureData: '',
  editAccountComponent: null,
}

export default MyAccountMobile
